import { toast } from 'sonner';
import { useRef, useState, useEffect, useCallback } from 'react';

import { useTranslate } from 'src/locales';
import { INSTAGRAM_APP_ID, INSTAGRAM_SCOPES } from 'src/config';

interface UseConnectInstagramProps {
  onSuccess: (data: { main_token: string; code?: string }) => void;
  onClick?: (value: any) => void;
  redirectUri?: string;
  appId?: string;
  targetOriginForPostMessage?: string;
}

export const useConnectInstagram = ({
  onSuccess,
  onClick,
  redirectUri = `${window.location.origin}/channel/step`,
  targetOriginForPostMessage = `${window.location.origin}`,
}: UseConnectInstagramProps) => {
  const [isInstagramConnectSuccess, setIsInstagramConnectSuccess] = useState(false);
  const [instagramToken, setInstagramToken] = useState<string | null>(null);
  const { t } = useTranslate('translation');

  // Refs to store listener + interval for cleanup
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const listenerRef = useRef<(event: MessageEvent) => void>();

  const cleanup = () => {
    if (listenerRef.current) {
      window.removeEventListener('message', listenerRef.current);
      listenerRef.current = undefined;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const connect = useCallback(() => {
    if (onClick) onClick(true);

    // INSTAGRAM_SCOPES is a string
    // "instagram_business_basic,instagram_business_manage_messages,..."

    // Convert string → array
    // Remove any single quotes and spaces, then split into array
    const scopes = INSTAGRAM_SCOPES?.replace(/'/g, '')?.split(',');

    const appId = INSTAGRAM_APP_ID;

    const authUrl = `https://api.instagram.com/oauth/authorize?client_id=${appId}&redirect_uri=${redirectUri}&scope=${scopes.join(',')}&response_type=code`;

    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl,
      'Instagram Login',
      `width=${width},height=${height},top=${top},left=${left}`
    );

    if (!popup) {
      toast.error(t('POPUP_BLOCKED'));
      return;
    }

    const messageListener = (event: MessageEvent) => {
      if (event.origin === targetOriginForPostMessage) {
        if (event.data?.type === 'instagram_auth_success' && event.data.code) {
          const code = event.data.code;
          setIsInstagramConnectSuccess(true);
          setInstagramToken(code);
          onSuccess({ main_token: code });
          toast.success(t('INSTAGRAM_LOGIN_SUCCESS'));
        } else {
          toast.error(t('INSTAGRAM_LOGIN_CANCELLED'));
        }
        popup.close();
        cleanup();
      } else {
        console.warn('Received message from untrusted origin:', event.origin);
      }
    };

    listenerRef.current = messageListener;
    window.addEventListener('message', messageListener);

    intervalRef.current = setInterval(() => {
      if (popup.closed) {
        cleanup(); // user closed popup
      }
    }, 500);
  }, [onClick, onSuccess, redirectUri, targetOriginForPostMessage, t]);

  // Cleanup if component unmounts
  // Cleanup if component unmounts
  useEffect(
    () => () => {
      cleanup();
    },
    []
  );

  return { connect, isInstagramConnectSuccess, instagramToken };
};
